import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";
import {
    Users, ShieldCheck, UserX, KeyRound, Activity, Search, X, ChevronRight,
    Copy, LogIn, Lock, Unlock, Ban, Trash2, RefreshCw, Monitor, Smartphone,
    Building2, Clock, FileText, ShieldAlert, ChevronDown, Plus,
} from "lucide-react";
import { Modal, Field } from "@/pages/Rooms";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const CREATE_ROLES = [
    { value: "hotel_admin", label: "Hotel Admin" },
    { value: "property_manager", label: "Property Manager" },
    { value: "reception", label: "Reception" },
    { value: "housekeeping", label: "Housekeeping" },
    { value: "pos_staff", label: "POS Staff" },
    { value: "finance_staff", label: "Finance" },
];

const ROLES = [
    { value: "all", label: "All roles" },
    { value: "super_admin", label: "Super Admin" },
    { value: "hotel_admin", label: "Hotel Admin" },
    { value: "property_manager", label: "Property Manager" },
    { value: "reception", label: "Reception" },
    { value: "housekeeping", label: "Housekeeping" },
    { value: "pos_staff", label: "POS Staff" },
    { value: "finance_staff", label: "Finance" },
];

const ROLE_COLORS = {
    super_admin: "bg-orange-100 text-orange-800 border-orange-200",
    hotel_admin: "bg-blue-100 text-blue-800 border-blue-200",
    property_manager: "bg-indigo-100 text-indigo-800 border-indigo-200",
    reception: "bg-teal-100 text-teal-800 border-teal-200",
    housekeeping: "bg-amber-100 text-amber-800 border-amber-200",
    pos_staff: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    finance_staff: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function KPI({ label, value, icon: Icon, tone = "orange", testid }) {
    const toneClass = {
        orange: "bg-orange-50 text-orange-700",
        blue: "bg-blue-50 text-blue-700",
        green: "bg-emerald-50 text-emerald-700",
        red: "bg-red-50 text-red-700",
        slate: "bg-slate-100 text-slate-700",
    }[tone];
    return (
        <div className="akm-card p-4" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className={`grid h-8 w-8 place-items-center rounded-md ${toneClass}`}><Icon size={16} /></div>
            </div>
            <div className="mt-2 text-2xl font-semibold tracking-tight">{value}</div>
        </div>
    );
}

function RolePill({ role }) {
    const cls = ROLE_COLORS[role] || "bg-slate-100 text-slate-700 border-slate-200";
    return (
        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium capitalize ${cls}`}>
            {role?.replace("_", " ")}
        </span>
    );
}

export default function SuperUsers() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [list, setList] = useState({ items: [], total: 0, page: 1, page_size: 50 });
    const [loading, setLoading] = useState(false);
    const [hotels, setHotels] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    // filters
    const [search, setSearch] = useState("");
    const [role, setRole] = useState("all");
    const [status, setStatus] = useState("all");
    const [hotelId, setHotelId] = useState("all");
    const [page, setPage] = useState(1);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/superadmin/users/stats");
            setStats(data);
        } catch { /* noop */ }
    }, []);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: 50 };
            if (search) params.search = search;
            if (role !== "all") params.role = role;
            if (status !== "all") params.status = status;
            if (hotelId !== "all") params.hotel_id = hotelId;
            const { data } = await api.get("/superadmin/users", { params });
            setList(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [page, search, role, status, hotelId]);

    useEffect(() => { api.get("/hotels").then(({ data }) => setHotels(data)); }, []);
    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => {
        const t = setTimeout(() => loadUsers(), search ? 250 : 0);
        return () => clearTimeout(t);
    }, [loadUsers, search]);

    const refresh = async () => { await Promise.all([loadStats(), loadUsers()]); };

    const totalPages = Math.max(1, Math.ceil(list.total / (list.page_size || 50)));

    return (
        <>
            <Topbar
                title={t("super_admin.users_title")}
                subtitle={`${list.total} ${t("common.total", "users")}`}
                actions={
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCreateOpen(true)} className="akm-btn-primary" data-testid="users-create-btn">
                            <Plus size={14} /> Create user
                        </button>
                        <button onClick={refresh} className="akm-btn-ghost" data-testid="users-refresh-btn">
                            <RefreshCw size={14} /> Refresh
                        </button>
                    </div>
                }
            />
            <div className="flex-1 overflow-auto p-6">
                {/* KPI Row */}
                <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                    <KPI testid="kpi-total" label="Total Users" value={stats?.total ?? "—"} icon={Users} tone="orange" />
                    <KPI testid="kpi-active" label="Active" value={stats?.active ?? "—"} icon={ShieldCheck} tone="green" />
                    <KPI testid="kpi-disabled" label="Disabled" value={stats?.disabled ?? "—"} icon={UserX} tone="slate" />
                    <KPI testid="kpi-locked" label="Locked" value={stats?.locked ?? "—"} icon={Lock} tone="red" />
                    <KPI testid="kpi-sessions" label="Active Sessions" value={stats?.active_sessions ?? "—"} icon={Activity} tone="blue" />
                    <KPI testid="kpi-new" label="New (30d)" value={stats?.new_30d ?? "—"} icon={KeyRound} tone="orange" />
                </div>

                {/* Filter Bar */}
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            data-testid="users-search"
                            value={search}
                            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                            placeholder="Search by name or email…"
                            className="akm-input h-9 w-[280px] pl-8"
                        />
                    </div>
                    <select data-testid="users-filter-role" value={role} onChange={(e) => { setPage(1); setRole(e.target.value); }} className="akm-input h-9 w-[180px]">
                        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <select data-testid="users-filter-status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="akm-input h-9 w-[160px]">
                        <option value="all">All status</option>
                        <option value="active">Active</option>
                        <option value="disabled">Disabled</option>
                        <option value="locked">Locked</option>
                    </select>
                    <select data-testid="users-filter-hotel" value={hotelId} onChange={(e) => { setPage(1); setHotelId(e.target.value); }} className="akm-input h-9 w-[220px]">
                        <option value="all">All tenants</option>
                        {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                    {(search || role !== "all" || status !== "all" || hotelId !== "all") && (
                        <button
                            data-testid="users-filter-clear"
                            onClick={() => { setSearch(""); setRole("all"); setStatus("all"); setHotelId("all"); setPage(1); }}
                            className="text-[12px] font-medium text-muted-foreground hover:text-foreground"
                        >Clear filters</button>
                    )}
                </div>

                {/* Table */}
                <div className="akm-card overflow-hidden">
                    <table className="akm-table w-full">
                        <thead className="bg-muted/40">
                            <tr>
                                <th>User</th>
                                <th>Role</th>
                                <th>Tenant</th>
                                <th>Status</th>
                                <th>Sessions</th>
                                <th>Created</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan="7" className="py-8 text-center text-muted-foreground">Loading…</td></tr>
                            )}
                            {!loading && list.items.length === 0 && (
                                <tr><td colSpan="7" className="py-12 text-center text-muted-foreground">No users match your filters</td></tr>
                            )}
                            {!loading && list.items.map((u) => (
                                <tr
                                    key={u.id}
                                    className="cursor-pointer hover:bg-muted/40"
                                    onClick={() => setSelectedUserId(u.id)}
                                    data-testid={`user-row-${u.id}`}
                                >
                                    <td>
                                        <div className="flex items-center gap-2.5">
                                            <div className="grid h-8 w-8 place-items-center rounded-full bg-orange-100 text-[11px] font-semibold uppercase text-orange-700">
                                                {(u.name || u.email || "?").slice(0, 2)}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="truncate font-medium text-foreground">{u.name || "—"}</div>
                                                <div className="truncate font-mono text-[11px] text-muted-foreground">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><RolePill role={u.role} /></td>
                                    <td className="max-w-[240px] truncate">{u.hotel_name || "Platform"}</td>
                                    <td>
                                        {u.is_locked ? (
                                            <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                                <Lock size={10} /> Locked
                                            </span>
                                        ) : (
                                            <StatusBadge status={u.is_active ? "active" : "skipped"} label={u.is_active ? "Active" : "Disabled"} />
                                        )}
                                    </td>
                                    <td>
                                        {u.active_sessions > 0 ? (
                                            <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                                <Activity size={10} /> {u.active_sessions}
                                            </span>
                                        ) : <span className="text-[12px] text-muted-foreground">—</span>}
                                    </td>
                                    <td className="text-muted-foreground">{formatDate(u.created_at)}</td>
                                    <td><ChevronRight size={14} className="text-muted-foreground" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {list.total > list.page_size && (
                    <div className="mt-3 flex items-center justify-between text-[12px] text-muted-foreground">
                        <div>Showing {(list.page - 1) * list.page_size + 1}–{Math.min(list.page * list.page_size, list.total)} of {list.total}</div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={list.page === 1} className="akm-btn-ghost h-7 px-2 text-[12px] disabled:opacity-40">Prev</button>
                            <span className="px-2">Page {list.page} of {totalPages}</span>
                            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={list.page >= totalPages} className="akm-btn-ghost h-7 px-2 text-[12px] disabled:opacity-40">Next</button>
                        </div>
                    </div>
                )}
            </div>

            {selectedUserId && (
                <UserDrawer
                    userId={selectedUserId}
                    onClose={() => setSelectedUserId(null)}
                    onChanged={refresh}
                />
            )}
            {createOpen && (
                <CreateUserModal
                    hotels={hotels}
                    onClose={() => setCreateOpen(false)}
                    onSaved={async () => { setCreateOpen(false); await refresh(); }}
                />
            )}
        </>
    );
}

function CreateUserModal({ hotels, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        role: "hotel_admin",
        hotel_id: hotels[0]?.id || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.hotel_id) {
            toast.error("Select a hotel for this user");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/users", form);
            toast.success("User created");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="Create user" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="create-user-form">
                <Field label="Hotel">
                    <select
                        className="akm-input"
                        required
                        value={form.hotel_id}
                        onChange={(e) => setForm({ ...form, hotel_id: e.target.value })}
                        data-testid="create-user-hotel"
                    >
                        <option value="">Select hotel…</option>
                        {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                </Field>
                <Field label="Name">
                    <input
                        className="akm-input"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="create-user-name"
                    />
                </Field>
                <Field label="Email">
                    <input
                        type="email"
                        className="akm-input"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="create-user-email"
                    />
                </Field>
                <Field label="Password">
                    <input
                        type="password"
                        className="akm-input"
                        required
                        minLength={6}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="create-user-password"
                    />
                </Field>
                <Field label="Role">
                    <select
                        className="akm-input"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="create-user-role"
                    >
                        {CREATE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" disabled={saving || !hotels.length} className="akm-btn-primary" data-testid="create-user-submit">
                        {saving ? "Saving…" : "Create"}
                    </button>
                </div>
                {!hotels.length && (
                    <p className="text-[12px] text-amber-700">No hotels found. Create a hotel first from the Hotels page.</p>
                )}
            </form>
        </Modal>
    );
}

// ================= DRAWER =================

const TABS = [
    { key: "overview", label: "Overview", icon: Users },
    { key: "permissions", label: "Permissions", icon: ShieldCheck },
    { key: "hotels", label: "Hotels", icon: Building2 },
    { key: "sessions", label: "Sessions", icon: Monitor },
    { key: "security", label: "Security", icon: ShieldAlert },
    { key: "activity", label: "Activity", icon: Clock },
    { key: "audit", label: "Audit Logs", icon: FileText },
];

function UserDrawer({ userId, onClose, onChanged }) {
    const [tab, setTab] = useState("overview");
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user: currentUser, startImpersonation } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/superadmin/users/${userId}`);
            setDetail(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            onClose();
        } finally {
            setLoading(false);
        }
    }, [userId, onClose]);

    useEffect(() => { load(); }, [load]);

    // ESC to close
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    const isSelf = detail?.id === currentUser?.id;

    const doImpersonate = async () => {
        if (isSelf) { toast.error("You cannot impersonate yourself"); return; }
        if (!window.confirm(`Impersonate ${detail.email}? Your Super Admin session will remain safely in the background.`)) return;
        const r = await startImpersonation(detail.id);
        if (r.ok) {
            toast.success(`Now acting as ${r.user.email}`);
            const dest = r.user.role === "super_admin" ? "/admin" : "/dashboard";
            window.location.href = dest;
        } else {
            toast.error(r.error || "Impersonation failed");
        }
    };

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
            <aside
                data-testid="user-drawer"
                className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[720px] flex-col overflow-hidden bg-background shadow-2xl"
                style={{ animation: "slideIn 220ms ease-out" }}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-border bg-card px-5 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-orange-100 text-sm font-semibold uppercase text-orange-700">
                            {(detail?.name || detail?.email || "?").slice(0, 2)}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <div className="truncate text-base font-semibold">{detail?.name || "—"}</div>
                                {detail && <RolePill role={detail.role} />}
                            </div>
                            <div className="mt-0.5 flex items-center gap-1 text-[12px] text-muted-foreground">
                                <span className="font-mono">{detail?.email}</span>
                                <button
                                    className="opacity-60 hover:opacity-100"
                                    title="Copy"
                                    onClick={() => { navigator.clipboard.writeText(detail?.email || ""); toast.success("Copied"); }}
                                ><Copy size={11} /></button>
                            </div>
                        </div>
                    </div>
                    <button data-testid="user-drawer-close" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
                </div>

                {/* Action bar */}
                <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-5 py-2.5">
                    <button
                        data-testid="user-impersonate-btn"
                        disabled={isSelf || !detail?.is_active}
                        onClick={doImpersonate}
                        className="akm-btn-primary h-8 text-[12px] disabled:opacity-40"
                        title={isSelf ? "Cannot impersonate yourself" : "Sign in as this user"}
                    >
                        <LogIn size={12} /> Impersonate
                    </button>
                    <TogglesBar detail={detail} isSelf={isSelf} onChanged={async () => { await load(); onChanged(); }} />
                </div>

                {/* Tabs */}
                <div className="flex gap-0.5 overflow-x-auto border-b border-border bg-card px-3">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                data-testid={`user-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${active ? "border-orange-500 text-orange-600" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            >
                                <Icon size={12} /> {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto p-5">
                    {loading || !detail ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
                    ) : (
                        <>
                            {tab === "overview" && <OverviewTab detail={detail} />}
                            {tab === "permissions" && <PermissionsTab detail={detail} />}
                            {tab === "hotels" && <HotelsTab detail={detail} />}
                            {tab === "sessions" && <SessionsTab userId={detail.id} onChanged={() => { load(); onChanged(); }} />}
                            {tab === "security" && <SecurityTab detail={detail} onChanged={async () => { await load(); onChanged(); }} />}
                            {tab === "activity" && <ActivityTab userId={detail.id} />}
                            {tab === "audit" && <AuditTab userId={detail.id} />}
                        </>
                    )}
                </div>
            </aside>
            <style>{`@keyframes slideIn { from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        </>
    );
}

function TogglesBar({ detail, isSelf, onChanged }) {
    if (!detail) return null;
    const toggleActive = async () => {
        if (isSelf) { toast.error("Cannot disable yourself"); return; }
        const to = !detail.is_active;
        if (!window.confirm(`${to ? "Enable" : "Disable"} this user?`)) return;
        try {
            await api.patch(`/superadmin/users/${detail.id}`, { is_active: to });
            toast.success(to ? "User enabled" : "User disabled — sessions revoked");
            onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const unlock = async () => {
        try {
            await api.post(`/superadmin/users/${detail.id}/unlock`);
            toast.success("User unlocked");
            onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const delUser = async () => {
        if (isSelf) { toast.error("Cannot delete yourself"); return; }
        if (!window.confirm(`Permanently delete ${detail.email}? All their sessions will be revoked.`)) return;
        try {
            await api.delete(`/superadmin/users/${detail.id}`);
            toast.success("User deleted");
            onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <>
            <button data-testid="user-toggle-active" onClick={toggleActive} disabled={isSelf} className="akm-btn-ghost h-8 text-[12px] disabled:opacity-40">
                {detail.is_active ? <><Ban size={12} /> Disable</> : <><ShieldCheck size={12} /> Enable</>}
            </button>
            {detail.is_locked && (
                <button data-testid="user-unlock-btn" onClick={unlock} className="akm-btn-ghost h-8 text-[12px]"><Unlock size={12} /> Unlock</button>
            )}
            <div className="ml-auto">
                <button data-testid="user-delete-btn" onClick={delUser} disabled={isSelf || detail.role === "super_admin"} className="akm-btn-ghost h-8 text-[12px] text-red-600 hover:bg-red-50 disabled:opacity-40">
                    <Trash2 size={12} /> Delete
                </button>
            </div>
        </>
    );
}

// ---------- Tabs ----------
function InfoRow({ label, children }) {
    return (
        <div className="grid grid-cols-[160px_1fr] gap-3 py-2.5 border-b border-border last:border-0">
            <div className="text-[12px] text-muted-foreground">{label}</div>
            <div className="text-[13px] text-foreground">{children}</div>
        </div>
    );
}

function OverviewTab({ detail }) {
    return (
        <div className="space-y-6">
            <div className="akm-card px-5 py-2">
                <InfoRow label="User ID"><span className="font-mono text-[11px]">{detail.id}</span></InfoRow>
                <InfoRow label="Email">{detail.email}</InfoRow>
                <InfoRow label="Full Name">{detail.name || "—"}</InfoRow>
                <InfoRow label="Role"><RolePill role={detail.role} /></InfoRow>
                <InfoRow label="Tenant">{detail.hotel?.name || "Platform"}</InfoRow>
                <InfoRow label="Status">
                    {detail.is_locked
                        ? <span className="text-red-700">Locked until {formatDate(detail.locked_until)}</span>
                        : detail.is_active ? <span className="text-emerald-700">Active</span> : <span className="text-slate-600">Disabled</span>}
                </InfoRow>
                <InfoRow label="Active Sessions">{detail.active_sessions}</InfoRow>
                <InfoRow label="Last Login">{detail.last_login ? `${formatDate(detail.last_login.created_at)} · ${detail.last_login.ip || "—"}` : "Never"}</InfoRow>
                <InfoRow label="Created">{formatDate(detail.created_at)}</InfoRow>
                <InfoRow label="Must Change Password">{detail.must_change_password ? "Yes" : "No"}</InfoRow>
            </div>
        </div>
    );
}

const ROLE_MODULES = {
    super_admin: ["*"],
    hotel_admin: ["dashboard", "properties", "rooms", "reservations", "guests", "staff", "housekeeping", "pos", "channels", "reports", "audit", "settings", "billing"],
    property_manager: ["dashboard", "properties", "rooms", "reservations", "guests", "housekeeping", "pos", "reports"],
    reception: ["dashboard", "reservations", "guests", "rooms"],
    housekeeping: ["dashboard", "housekeeping", "rooms"],
    pos_staff: ["dashboard", "pos"],
    finance_staff: ["dashboard", "reports", "billing"],
};

const ALL_MODULES = ["dashboard", "properties", "rooms", "reservations", "guests", "staff", "housekeeping", "pos", "channels", "reports", "audit", "settings", "billing"];

function PermissionsTab({ detail }) {
    const perms = ROLE_MODULES[detail.role] || [];
    const wildcard = perms.includes("*");
    return (
        <div className="space-y-4">
            <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-[12px] text-orange-800">
                Permissions are derived from role. Change role from the Security tab to adjust access.
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {ALL_MODULES.map((m) => {
                    const has = wildcard || perms.includes(m);
                    return (
                        <div key={m} className={`flex items-center justify-between rounded-md border px-3 py-2 ${has ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40"}`}>
                            <span className="text-[13px] capitalize text-foreground">{m.replace("_", " ")}</span>
                            {has
                                ? <ShieldCheck size={14} className="text-emerald-600" />
                                : <X size={14} className="text-slate-400" />}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function HotelsTab({ detail }) {
    if (detail.role === "super_admin") {
        return <div className="rounded-md border border-orange-200 bg-orange-50 p-4 text-[13px] text-orange-800">
            Super Admin has access to all tenants and properties across the platform.
        </div>;
    }
    return (
        <div className="space-y-4">
            <div className="akm-card p-4">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Tenant</div>
                <div className="mt-1 flex items-center gap-2">
                    <Building2 size={16} className="text-orange-500" />
                    <div>
                        <div className="font-semibold">{detail.hotel?.name || "—"}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{detail.hotel?.slug || detail.hotel_id || "—"}</div>
                    </div>
                </div>
            </div>
            <div>
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Assigned Properties</div>
                {detail.properties?.length ? (
                    <div className="akm-card divide-y divide-border">
                        {detail.properties.map((p) => (
                            <div key={p.id} className="flex items-center justify-between px-4 py-2.5">
                                <div>
                                    <div className="text-[13px] font-medium">{p.name}</div>
                                    <div className="text-[11px] text-muted-foreground">{p.city || "—"}</div>
                                </div>
                                <span className="font-mono text-[11px] text-muted-foreground">{p.id.slice(0, 8)}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">No specific property assignments — access follows tenant scope</div>
                )}
            </div>
        </div>
    );
}

function DeviceIcon({ device }) {
    return device === "Mobile" ? <Smartphone size={14} /> : <Monitor size={14} />;
}

function SessionsTab({ userId, onChanged }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/superadmin/users/${userId}/sessions`);
            setSessions(data);
        } finally { setLoading(false); }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    const revoke = async (sid) => {
        if (!window.confirm("Revoke this session? The user will be signed out on that device immediately.")) return;
        try {
            await api.post(`/superadmin/users/${userId}/sessions/${sid}/revoke`);
            toast.success("Session revoked");
            load(); onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const revokeAll = async () => {
        if (!window.confirm("Revoke ALL active sessions for this user? They will be signed out everywhere.")) return;
        try {
            const { data } = await api.post(`/superadmin/users/${userId}/sessions/revoke-all`);
            toast.success(`Revoked ${data.revoked_count} session(s)`);
            load(); onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const active = sessions.filter((s) => s.is_active);
    const past = sessions.filter((s) => !s.is_active);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">{active.length} active · {past.length} past</div>
                {active.length > 0 && (
                    <button data-testid="sessions-revoke-all" onClick={revokeAll} className="akm-btn-ghost h-8 text-[12px] text-red-600 hover:bg-red-50">
                        <Ban size={12} /> Revoke all
                    </button>
                )}
            </div>

            {loading && <div className="py-4 text-center text-[12px] text-muted-foreground">Loading…</div>}
            {!loading && sessions.length === 0 && <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">No sessions recorded yet</div>}

            {active.length > 0 && (
                <div>
                    <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Active</div>
                    <div className="akm-card divide-y divide-border">
                        {active.map((s) => (
                            <SessionRow key={s.id} s={s} active onRevoke={() => revoke(s.id)} />
                        ))}
                    </div>
                </div>
            )}
            {past.length > 0 && (
                <div>
                    <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Past sessions</div>
                    <div className="akm-card divide-y divide-border">
                        {past.slice(0, 20).map((s) => <SessionRow key={s.id} s={s} />)}
                    </div>
                </div>
            )}
        </div>
    );
}

function SessionRow({ s, active, onRevoke }) {
    return (
        <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className={`grid h-8 w-8 place-items-center rounded-md ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    <DeviceIcon device={s.user_agent?.device} />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[13px] font-medium">
                        {s.user_agent?.browser || "Unknown"} · {s.user_agent?.os || "?"}
                        {s.impersonated_by && (
                            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800">IMPERSONATED</span>
                        )}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                        IP {s.ip_address || "—"} · Started {formatDate(s.created_at)} · Last seen {formatDate(s.last_seen_at)}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {active
                    ? <button data-testid={`session-revoke-${s.id}`} onClick={onRevoke} className="akm-btn-ghost h-7 text-[11px] text-red-600 hover:bg-red-50">Revoke</button>
                    : <span className="text-[11px] text-muted-foreground">{s.is_revoked ? "Revoked" : "Expired"}</span>
                }
            </div>
        </div>
    );
}

function SecurityTab({ detail, onChanged }) {
    const [role, setRole] = useState(detail.role);
    const [saving, setSaving] = useState(false);
    const [tempPassword, setTempPassword] = useState(null);

    const changeRole = async () => {
        if (role === detail.role) return;
        if (!window.confirm(`Change role from "${detail.role}" to "${role}"?`)) return;
        setSaving(true);
        try {
            await api.patch(`/superadmin/users/${detail.id}`, { role });
            toast.success("Role updated");
            onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const resetPwd = async () => {
        if (!window.confirm("Generate a temporary password for this user? All active sessions will be revoked and they will be forced to change it on next login.")) return;
        try {
            const { data } = await api.post(`/superadmin/users/${detail.id}/reset-password`);
            setTempPassword(data.temporary_password);
            toast.success("Temporary password generated");
            onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-5">
            <div className="akm-card p-4">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Role</div>
                <div className="flex items-center gap-2">
                    <select data-testid="security-role-select" value={role} onChange={(e) => setRole(e.target.value)} className="akm-input h-9 flex-1">
                        {ROLES.filter((r) => r.value !== "all").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button data-testid="security-save-role" disabled={role === detail.role || saving} onClick={changeRole} className="akm-btn-primary h-9 disabled:opacity-40">
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            <div className="akm-card p-4">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Password</div>
                <div className="mb-3 text-[12px] text-muted-foreground">Generate a temporary password. All active sessions will be revoked and the user must change it on next login.</div>
                <button data-testid="security-reset-password" onClick={resetPwd} className="akm-btn-ghost h-9"><KeyRound size={13} /> Generate temporary password</button>

                {tempPassword && (
                    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
                        <div className="text-[11px] font-medium uppercase tracking-wider text-amber-800">Temporary password (share out-of-band, shown only once)</div>
                        <div className="mt-2 flex items-center gap-2">
                            <code className="flex-1 rounded bg-white px-2 py-1.5 font-mono text-[13px] text-amber-900">{tempPassword}</code>
                            <button onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success("Copied"); }} className="akm-btn-ghost h-8 text-[12px]"><Copy size={12} /> Copy</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="akm-card p-4">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Account lock</div>
                {detail.is_locked ? (
                    <div className="text-[12px] text-red-700">This account is locked until {formatDate(detail.locked_until)}. Use "Unlock" in the toolbar above to release it.</div>
                ) : (
                    <div className="text-[12px] text-muted-foreground">
                        Account is not locked. Failed login attempts: <span className="font-mono font-medium">{detail.failed_login_count || 0}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function ActivityTab({ userId }) {
    const [data, setData] = useState({ logins: [], admin_actions: [] });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/superadmin/users/${userId}/activity`).then(({ data }) => setData(data)).finally(() => setLoading(false));
    }, [userId]);
    if (loading) return <div className="py-4 text-center text-[12px] text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-5">
            <div>
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Login history</div>
                {data.logins.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">No login events</div>
                ) : (
                    <div className="akm-card divide-y divide-border">
                        {data.logins.slice(0, 30).map((l) => (
                            <div key={l.id} className="flex items-center justify-between px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                    {l.action === "login"
                                        ? <span className="grid h-6 w-6 place-items-center rounded-md bg-emerald-50 text-emerald-700"><LogIn size={12} /></span>
                                        : <span className="grid h-6 w-6 place-items-center rounded-md bg-slate-100 text-slate-600"><Ban size={12} /></span>}
                                    <div>
                                        <div className="text-[13px] font-medium capitalize">{l.action}</div>
                                        <div className="text-[11px] text-muted-foreground">IP {l.ip || "—"}</div>
                                    </div>
                                </div>
                                <div className="text-[11px] text-muted-foreground">{formatDate(l.created_at)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div>
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Admin actions on this account</div>
                {data.admin_actions.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">No admin actions</div>
                ) : (
                    <div className="akm-card divide-y divide-border">
                        {data.admin_actions.slice(0, 30).map((a) => (
                            <div key={a.id} className="flex items-center justify-between px-4 py-2.5">
                                <div>
                                    <div className="text-[13px] font-medium capitalize">{a.action.replace("_", " ")}</div>
                                    <div className="text-[11px] text-muted-foreground">by {a.user_email || "system"}</div>
                                </div>
                                <div className="text-[11px] text-muted-foreground">{formatDate(a.created_at)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AuditTab({ userId }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/superadmin/users/${userId}/audit-logs?limit=200`).then(({ data }) => setLogs(data)).finally(() => setLoading(false));
    }, [userId]);
    if (loading) return <div className="py-4 text-center text-[12px] text-muted-foreground">Loading…</div>;
    if (logs.length === 0) return <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">No audit trail yet</div>;
    return (
        <div className="akm-card divide-y divide-border">
            {logs.map((l) => (
                <details key={l.id} className="group px-4 py-2.5">
                    <summary className="flex cursor-pointer items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase text-foreground">{l.action}</span>
                            <span className="text-[13px] font-medium capitalize">{l.resource}</span>
                            {l.resource_id && <span className="font-mono text-[10px] text-muted-foreground">{l.resource_id.slice(0, 8)}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-muted-foreground">{formatDate(l.created_at)}</span>
                            <ChevronDown size={12} className="text-muted-foreground group-open:rotate-180 transition" />
                        </div>
                    </summary>
                    {l.metadata && Object.keys(l.metadata).length > 0 && (
                        <pre className="mt-2 overflow-x-auto rounded-md bg-muted/40 p-2 font-mono text-[10px] text-muted-foreground">{JSON.stringify(l.metadata, null, 2)}</pre>
                    )}
                </details>
            ))}
        </div>
    );
}
